import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';

import { useValidateImage } from '@/hooks';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { IMAGE_UPLOAD_CONSTANTS } from '@/views/build/constant';

import { openImageSearchFailDialog } from '../utils/imageSearchFailDialog';

const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

const API_ORIGIN = process.env.NEXT_PUBLIC_SERVER_BASEURL ?? '';

// 서버가 확정해 내려준 imageFetchUrl만 fetch한다.
//  - pexels: CDN 절대 URL을 그대로 fetch (인증 불필요)
//  - naver: API 서버의 "/api/image-proxy?token=…" 상대경로. 프록시가 퀴즈 서버로
//    흡수돼 JWT로 게이트되므로, API 오리진을 붙이고 Bearer 토큰을 실어 fetch한다.
//    프록시는 서버가 서명한 토큰만 신뢰한다(SSRF/open proxy 차단).
const fetchSearchedImage = (imageFetchUrl: string): Promise<Response> => {
  // 절대 URL(pexels CDN)은 인증 없이 그대로 fetch — 타 오리진에 JWT를 흘리지 않는다.
  if (/^https?:\/\//i.test(imageFetchUrl)) {
    return fetch(imageFetchUrl);
  }
  const apiToken = ApiTokenStorage.get();
  return fetch(`${API_ORIGIN}${imageFetchUrl}`, {
    credentials: 'include',
    headers: apiToken ? { Authorization: `Bearer ${apiToken}` } : undefined,
  });
};
export const useSearchedImageFile = () => {
  const validate = useValidateImage();
  const { t } = useTranslation(['build', 'common']);

  const {
    ALLOWED_IMAGE_EXTENSIONS,
    MAX_IMAGE_DIMENSION,
    MAX_MB_SIZE,
    MAX_IMAGE_COUNT,
  } = IMAGE_UPLOAD_CONSTANTS;

  return useCallback(
    async (imageFetchUrl: string) => {
      try {
        const response = await fetchSearchedImage(imageFetchUrl);

        // Check if the image is accessible
        if (!response.ok) {
          openImageSearchFailDialog(t, 'failed');
          return null;
        }

        const blob = await response.blob();

        // Determine file extension from MIME type
        const extension = MIME_TO_EXTENSION[blob.type] || 'jpg';

        const file = new File([blob], `searched-image.${extension}`, {
          type: blob.type,
        });
        const isImageValid = await validate({
          file,
          validation: {
            mbSize: MAX_MB_SIZE,
            maxDimension: MAX_IMAGE_DIMENSION,
            extensions: ALLOWED_IMAGE_EXTENSIONS,
            countLimit: MAX_IMAGE_COUNT,
          },
        });

        if (!isImageValid) {
          return null;
        }
        return file;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [
      validate,
      t,
      ALLOWED_IMAGE_EXTENSIONS,
      MAX_IMAGE_DIMENSION,
      MAX_MB_SIZE,
      MAX_IMAGE_COUNT,
    ],
  );
};
